import {
  AthleteActualWorkoutDomain,
  WorkoutLayerDomain,
} from '@strength/domain';
import {
  HasWorkoutLayer,
  mergeAthleteWorkoutLayers,
} from '@strength/domain-workout';
import { WorkoutLayer } from '@strength/db-schema';
import {
  WorkoutLayerCommand,
  WorkoutLayerIdentity,
} from '../mutation-fns/types';
import { v4 } from 'uuid';
import {
  createWorkoutLayerEdit,
  editExerciseEdits,
  editSetMetricEdits,
} from '../mutation-fns/workout-draft';
import {
  AthleteHasWorkoutLayer,
  WorkoutMutationContext,
} from '../../actuals/types';
import { WorkoutSummaryActualsMutationFns } from './types';

export function runAthleteActualsMutations(
  context: WorkoutMutationContext,
  commands: WorkoutLayerCommand[],
  bulkMutate: (commands: readonly AthleteActualWorkoutDomain.Command[]) => void,
  additionalLayers?: {
    athletePublishedWorkoutLayer?: WorkoutLayer;
    blockPublishedWorkoutLayer?: WorkoutLayer;
    athleteModifiedPublishedWorkoutLayer?: WorkoutLayer;
  },
) {
  const athleteActualWorkoutCommands: AthleteActualWorkoutDomain.Command[] = [];

  for (const command of commands) {
    // AthleteActualWorkoutDomain.Edit spirtitually is a subclass of WorkoutLayerDomain.HasWorkoutLayerEdit, which makes this a safe cast
    const edits = command.edits as AthleteActualWorkoutDomain.Edit[];
    const { exists, workoutId, id } = command.identity;
    const { blockId, profileId, athleteVersion, blockVersion, assignedDate } =
      context;

    if (!exists) {
      edits.unshift({
        modelName: 'AthleteActualWorkoutLayer',
        modelId: id,
        action: 'create',
        data: {
          layer: WorkoutLayerDomain.defaultLayer(),
        },
      });
    }

    // Find the last non-delete edit to get the final layer state
    // When !exists, edits[0] is the create edit with defaultLayer(), but subsequent edits contain the actual changes
    let athleteActualWorkoutLayer: WorkoutLayer | undefined;
    for (let i = edits.length - 1; i >= 0; i--) {
      const edit = edits[i];
      if (
        edit &&
        edit.action !== 'delete' &&
        'data' in edit &&
        edit.data?.layer
      ) {
        athleteActualWorkoutLayer = edit.data.layer;
        break;
      }
    }

    // A manufactured empty day has no block layer keyed to the synthetic
    // workoutId, so the shape and delivery-type tags come from the layer the
    // row was synthesized from instead.
    const blockPublishedWorkoutLayer =
      additionalLayers?.blockPublishedWorkoutLayer ?? context.syntheticLayer;

    // athleteVersion 0 means no published athlete layer exists yet, so the
    // actual layer starts empty and holds only the edited metric. The server
    // resolves the entered Match/Training counts from the outgoing layer's
    // delivery-type tags, so carry the block's tags alongside the edit.
    const resolvedLayer =
      athleteVersion === 0
        ? withDeliveryTypeTags(
            athleteActualWorkoutLayer,
            blockPublishedWorkoutLayer,
          )
        : athleteActualWorkoutLayer;

    if (athleteVersion === 0 && resolvedLayer) {
      for (const edit of edits) {
        if (edit.action !== 'delete') edit.data.layer = resolvedLayer;
      }
    }

    const workout = mergeAthleteWorkoutLayers(
      blockPublishedWorkoutLayer
        ? {
            layer: blockPublishedWorkoutLayer,
            workoutId,
            id: '',
          }
        : undefined,
      additionalLayers?.athletePublishedWorkoutLayer
        ? {
            layer: additionalLayers.athletePublishedWorkoutLayer,
            workoutId,
            id: '',
          }
        : undefined,
      additionalLayers?.athleteModifiedPublishedWorkoutLayer
        ? {
            layer: additionalLayers.athleteModifiedPublishedWorkoutLayer,
            workoutId,
            id: '',
          }
        : undefined,
      resolvedLayer
        ? {
            layer: resolvedLayer,
            workoutId,
            id: '',
          }
        : undefined,
    );

    athleteActualWorkoutCommands.push({
      module: AthleteActualWorkoutDomain.Module,
      identity: {
        workoutId,
        blockId,
        profileId,
        athleteVersion,
        blockVersion,
        // Only the manufactured-day sentinel carries the date; the other
        // athleteVersion 0 path lets the server derive it from the block layer.
        ...(athleteVersion === 0 && assignedDate ? { assignedDate } : {}),
      },
      edits,
      workout,
    });
  }

  bulkMutate(athleteActualWorkoutCommands);
}

// Delivery types the server resolves when fanning a manual entry out to the
// sibling bowling workouts.
const DELIVERY_TYPE_TAGS = ['Match', 'Training'] as const;

/**
 * Copies the block layer's delivery-type tag metrics (Match/Training) into the
 * actual layer when missing. The server matches each entered count to its set
 * by reading these tags off the outgoing layer, so an empty actual layer would
 * otherwise only carry the edited metric and the value would not fan out.
 */
function withDeliveryTypeTags(
  actualLayer: WorkoutLayer | undefined,
  blockLayer: WorkoutLayer | undefined,
): WorkoutLayer | undefined {
  if (!actualLayer || !blockLayer) return actualLayer;

  const existingKeys = new Set(
    actualLayer.setMetrics.map((m) => `${m.workoutSetId}|${m.metricId}`),
  );
  const tags = blockLayer.setMetrics.filter(
    (m) =>
      m.input != null &&
      (DELIVERY_TYPE_TAGS as readonly string[]).includes(m.input) &&
      !existingKeys.has(`${m.workoutSetId}|${m.metricId}`),
  );

  if (tags.length === 0) return actualLayer;

  return {
    ...actualLayer,
    setMetrics: [...actualLayer.setMetrics, ...tags],
  };
}

export function makeActualWorkoutLayerMutationFns(
  workoutLayers: AthleteHasWorkoutLayer[],
  mutateFn: (
    context: WorkoutMutationContext,
    command: WorkoutLayerCommand,
  ) => void,
): WorkoutSummaryActualsMutationFns {
  function createMutateFn<FPARAM extends unknown[], MPARAM extends unknown[]>(
    fn: (...params: FPARAM) => WorkoutLayerDomain.WorkoutLayerEdit[],
    includeLayer = false,
  ) {
    return (
      context: WorkoutMutationContext,
      workoutId: string,
      ...params: MPARAM
    ) => {
      const { identity, workoutLayer } = findWorkoutLayer(
        workoutLayers,
        workoutId,
      );
      const fnParams = includeLayer ? [workoutLayer, ...params] : params;
      const edits = fn(...(fnParams as FPARAM));

      if (!edits?.length) return;

      const workoutLayerEdit = createWorkoutLayerEdit(
        identity,
        workoutLayer,
        edits,
      );
      mutateFn(context, workoutLayerEdit);
    };
  }

  return {
    onEditSetMetric: createMutateFn(editSetMetricEdits, true),
    onEditExercise: createMutateFn(editExerciseEdits, true),
  };
}

function findWorkoutLayer(
  workoutLayers: HasWorkoutLayer[],
  workoutId: string,
): {
  workoutLayer: HasWorkoutLayer | undefined;
  identity: WorkoutLayerIdentity;
} {
  const workoutLayer = workoutLayers.find((m) => m.workoutId === workoutId);

  if (!workoutLayer) {
    return {
      workoutLayer,
      identity: {
        workoutId,
        id: v4(),
        exists: false,
        modelName: 'AthleteActualWorkoutLayer',
      },
    };
  } else {
    return {
      workoutLayer,
      identity: {
        workoutId,
        id: workoutLayer.id,
        exists: true,
        modelName: 'AthleteActualWorkoutLayer',
      },
    };
  }
}
